import { useGetDependentRiskAssessmentOptionsLazyQuery } from '@/__generated__/graphql';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { Filter, FilterType } from '@/types';
import { useEffect, useMemo, useRef, useState } from 'react';
import { riskAssessmentAssemblyTemplate } from '../../state';
import { NOT_APPLICABLE_LABEL } from '@/constants';
import { useAtomValue } from 'jotai';

const summaryFilteredFields = new Set([
  'risk_level_1_component',
  'risk_level_2_component',
  'risk_structure',
  'risk_damage_class',
]);

const reviewFilteredFields = new Set([
  'risk_level_1_component',
  'risk_level_2_component',
  'risk_structure',
  'risk_component_type',
  'risk_damage_class',
  'risk_unmitigated_she_category',
  'risk_unmitigated_financials_category',
]);

const staticOptionFields = new Set([
  'risk_damage_class',
  'risk_unmitigated_she_category',
  'risk_unmitigated_financials_category',
]);
const multiSelectFilters = new Set([
  'risk_structure',
  'risk_damage_class',
  'risk_member_id',
  'risk_unmitigated_she_category',
  'risk_unmitigated_financials_category',
]);

const { SINGLE_SELECT, MULTI_SELECT } = FilterType;

const getFilteredFields = {
  summary: summaryFilteredFields,
  review: reviewFilteredFields,
};

export const useFiltersForRisk = ({
  source,
  selectedFilters,
}: {
  source: 'summary' | 'review' | 'calculations';
  selectedFilters: { id: string; value: string }[];
}) => {
  const [filters, setFilters] = useState<Filter[]>([]);
  const template = useAtomValue(riskAssessmentAssemblyTemplate);
  const riskStructureOptions = useRef<{ key: string; label: string }[]>([]);
  const structureId = useGetStructureId();

  const initialFilters = useMemo(() => {
    let fields = template?.fields;
    if (!fields) return [];
    if (source === 'calculations') {
      fields = fields.filter(
        ({ displayInRisk, key }) => displayInRisk || key === 'risk_damage_class'
      );
    } else {
      fields = fields.filter(({ key }) => key && getFilteredFields[source].has(key));
    }

    return fields.map(({ key, name, options }) => ({
      key: key!!,
      label: name,
      type: multiSelectFilters.has(key ?? '') ? MULTI_SELECT : SINGLE_SELECT,
      options: options.map((option) => ({ key: option, label: option })),
    }));
  }, [source, template?.fields]);

  useEffect(() => {
    setFilters(initialFilters);
  }, [initialFilters]);

  const [fetchDependentOptions, { data, loading }] = useGetDependentRiskAssessmentOptionsLazyQuery({
    variables: {
      input: {
        structureId,
        templateId: template?.id ?? '',
        selectedFilters: selectedFilters.filter(({ id }) => id !== 'risk_member_id'),
      },
    },
  });

  useEffect(() => {
    if (selectedFilters.length > 0) fetchDependentOptions();
  }, [fetchDependentOptions, selectedFilters.length, template]);

  const dependentOptions = useMemo(() => {
    return data?.getDependentRiskAssessmentOptions ?? [];
  }, [data?.getDependentRiskAssessmentOptions]);

  useEffect(() => {
    if (selectedFilters.length === 0) {
      setFilters(initialFilters);
      return;
    }
    if (dependentOptions.length === 0) return;

    const isRiskStructureFilterSelected = selectedFilters.some(({ id }) => id === 'risk_structure');

    if (!isRiskStructureFilterSelected) {
      riskStructureOptions.current =
        dependentOptions
          .find((filter) => filter?.key === 'risk_structure')
          ?.options?.filter((option) => option !== null)
          .map((option) => ({ key: option!!, label: option!! })) ?? [];
    }

    setFilters((currentFilters) => {
      return currentFilters.map((filter) => {
        if (staticOptionFields.has(filter.key)) {
          return filter;
        }
        let options = (dependentOptions.find((option) => option?.key === filter.key)?.options ?? [])
          .filter((option) => option !== null)
          .map((option) => ({ key: option!!, label: option!! }));
        if (filter.key === 'risk_structure') options = riskStructureOptions.current;
        if (
          filter.key === 'risk_member_id' &&
          !options.map((option) => option.label).includes(NOT_APPLICABLE_LABEL)
        ) {
          options.push({ key: NOT_APPLICABLE_LABEL, label: NOT_APPLICABLE_LABEL });
        }
        return dependentOptions.length > 0 ? { ...filter, options } : filter;
      });
    });
  }, [dependentOptions, initialFilters, selectedFilters, selectedFilters.length, template]);

  return { filters, loading };
};
